import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EntityField, FilterValue, getFilterableFields } from '../utils/entityFields';
import SearchableSelect from './SearchableSelect';
import NumericInput from './NumericInput';
import './FilterBar.css';

export interface ForeignKeyOption {
  value: string;
  label: string;
}

interface FilterBarProps {
  fields: EntityField[];
  onFilterChange: (filters: FilterValue[]) => void;
  // Provide options for foreignKey fields (e.g., users, groups, admins)
  foreignKeyOptions?: Record<string, ForeignKeyOption[]>;
}

/**
 * Unified admin table filter row.
 *
 * Collapsed: a quick-search input (bound to the `name` filter) on the left and,
 * when the entity has structured filters, an "N filters active" badge + chevron
 * on the right. Clicking the row toggles the expandable panel; clicking inside
 * the search input does not.
 *
 * Expanded panel: the structured filters, all sharing the SearchableSelect
 * control visual language. The `name` field is excluded from the panel (the
 * quick search IS the name filter). When the only filterable field is `name`,
 * the component renders in search-only mode (no chevron / panel).
 */
const FilterBar: React.FC<FilterBarProps> = ({ fields, onFilterChange, foreignKeyOptions = {} }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string | boolean | null>>({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  // Memoized: a fresh array identity here would re-create buildFilters every
  // render, re-running the emit effect and looping with the parent's setState.
  const filterableFields = useMemo(() => getFilterableFields(fields), [fields]);
  // Structured filters shown in the expandable panel — everything except `name`,
  // which is handled by the quick-search input.
  const panelFields = filterableFields.filter(f => f.key !== 'name');
  const hasPanel = panelFields.length > 0;
  const nameField = filterableFields.find(f => f.key === 'name');
  const namePlaceholder = nameField?.placeholderKey
    ? t(nameField.placeholderKey)
    : t('common.search');

  // Convert filter values to FilterValue array
  const buildFilters = useCallback((values: Record<string, string | boolean | null>): FilterValue[] => {
    const filters: FilterValue[] = [];
    const processedDateFields = new Set<string>();

    // Map of field key -> field type so we can pick the right operator.
    const fieldTypeByKey = new Map(filterableFields.map(f => [f.key, f.type]));

    for (const [key, value] of Object.entries(values)) {
      // Handle date range compound keys (_from / _to)
      if (key.endsWith('_from') || key.endsWith('_to')) {
        const baseField = key.replace(/_from$|_to$/, '');
        if (processedDateFields.has(baseField)) continue;
        processedDateFields.add(baseField);

        const fromVal = values[`${baseField}_from`];
        const toVal = values[`${baseField}_to`];

        if ((fromVal && fromVal !== '') || (toVal && toVal !== '')) {
          filters.push({
            field: baseField,
            value: (fromVal as string) || null,
            valueTo: (toVal as string) || null,
            operator: 'dateRange',
          });
        }
        continue;
      }

      if (value !== '' && value !== null && value !== undefined) {
        // select / foreignKey / boolean filters must match exactly so that, e.g.,
        // 'weekly' does not also match 'biweekly' and activityId 5 does not match
        // 15. Text filters keep substring ('contains') matching.
        const fieldType = fieldTypeByKey.get(key);
        const operator = (fieldType === 'select' || fieldType === 'foreignKey' || fieldType === 'boolean')
          ? 'equals' as const
          : 'contains' as const;
        filters.push({ field: key, value, operator });
      }
    }

    return filters;
  }, [filterableFields]);

  // Update parent when filters change. Emit only when the built filters
  // actually differ from the last emission — guards against identity churn
  // (e.g. an inline `fields` prop) re-triggering the parent's setState loop.
  const lastEmittedRef = useRef<string | null>(null);
  useEffect(() => {
    const filters = buildFilters(filterValues);
    setActiveFilterCount(filters.length);
    const serialized = JSON.stringify(filters);
    if (serialized === lastEmittedRef.current) return;
    lastEmittedRef.current = serialized;
    onFilterChange(filters);
  }, [filterValues, buildFilters, onFilterChange]);

  const handleTextChange = (field: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [field]: value || null }));
  };

  const handleBooleanChange = (field: string, value: string) => {
    let boolValue: boolean | null = null;
    if (value === 'true') boolValue = true;
    if (value === 'false') boolValue = false;
    setFilterValues(prev => ({ ...prev, [field]: boolValue }));
  };

  const handleClearFilters = () => {
    setFilterValues({});
  };

  const renderFilterControl = (field: EntityField) => {
    const value = filterValues[field.key];
    const label = t(field.labelKey);
    const placeholder = field.placeholderKey ? t(field.placeholderKey) : `${t('filter.filterBy')} ${label}...`;

    switch (field.type) {
      case 'text':
      case 'array':
        return (
          <input
            type="text"
            placeholder={placeholder}
            value={(value as string) || ''}
            onChange={(e) => handleTextChange(field.key, e.target.value)}
            className="filter-bar__input"
          />
        );

      case 'date':
        return (
          <div className="filter-bar__date-range">
            <div className="filter-bar__date-field">
              <span className="filter-bar__date-label">{t('filter.from')}</span>
              <input
                type="date"
                value={(filterValues[`${field.key}_from`] as string) || ''}
                onChange={(e) => handleTextChange(`${field.key}_from`, e.target.value)}
                className="filter-bar__input filter-bar__input--date"
              />
            </div>
            <div className="filter-bar__date-field">
              <span className="filter-bar__date-label">{t('filter.to')}</span>
              <input
                type="date"
                value={(filterValues[`${field.key}_to`] as string) || ''}
                onChange={(e) => handleTextChange(`${field.key}_to`, e.target.value)}
                className="filter-bar__input filter-bar__input--date"
              />
            </div>
          </div>
        );

      case 'number':
        return (
          <NumericInput
            hideButtons
            placeholder={placeholder}
            value={(value as string) || ''}
            onChange={(e) => handleTextChange(field.key, e.target.value)}
            className="filter-bar__input"
          />
        );

      case 'select': {
        const selectOpts = field.options || [];
        return (
          <SearchableSelect
            options={selectOpts.map(opt => ({ _id: opt.value, name: t(opt.labelKey) }))}
            value={(value as string) || null}
            onChange={(val) => handleSelectChange(field.key, val == null ? '' : String(val))}
            placeholder={t('filter.all')}
            id={`filter-${field.key}`}
          />
        );
      }

      case 'foreignKey': {
        const fkOptions = field.foreignKeyEntity ? (foreignKeyOptions[field.foreignKeyEntity] || []) : [];
        return (
          <SearchableSelect
            options={fkOptions.map(o => ({ _id: o.value, name: o.label }))}
            value={(value as string) || null}
            onChange={(val) => handleSelectChange(field.key, val == null ? '' : String(val))}
            placeholder={t('filter.all')}
            id={`filter-${field.key}`}
          />
        );
      }

      case 'boolean':
        return (
          <SearchableSelect
            options={[
              { _id: 'true', name: t('common.yes') },
              { _id: 'false', name: t('common.no') },
            ]}
            value={value === true ? 'true' : value === false ? 'false' : null}
            onChange={(val) => handleBooleanChange(field.key, val == null ? '' : String(val))}
            placeholder={t('filter.all')}
            id={`filter-${field.key}`}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="filter-bar">
      <div
        className={`filter-bar__header${hasPanel ? ' filter-bar__header--clickable' : ''}`}
        onClick={hasPanel ? () => setIsExpanded(prev => !prev) : undefined}
        role={hasPanel ? 'button' : undefined}
        aria-expanded={hasPanel ? isExpanded : undefined}
        tabIndex={hasPanel ? 0 : undefined}
        onKeyDown={hasPanel ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            // Ignore key events originating from the search input.
            if ((e.target as HTMLElement).tagName === 'INPUT') return;
            e.preventDefault();
            setIsExpanded(prev => !prev);
          }
        } : undefined}
      >
        {/* Quick search (bound to the `name` filter). Clicks/typing here must not
            toggle the panel. */}
        <input
          type="text"
          className="filter-bar__search"
          value={(filterValues['name'] as string) || ''}
          onChange={(e) => handleTextChange('name', e.target.value)}
          onClick={(e) => e.stopPropagation()}
          placeholder={namePlaceholder}
          aria-label={namePlaceholder}
        />

        <div className="filter-bar__header-right">
          {activeFilterCount > 0 && (
            <button
              type="button"
              className="filter-bar__clear"
              onClick={(e) => { e.stopPropagation(); handleClearFilters(); }}
            >
              {t('filter.clear')}
            </button>
          )}
          {hasPanel && (
            <>
              {activeFilterCount > 0 && (
                <span className="filter-bar__badge">
                  {t('filter.activeCount', { count: activeFilterCount })}
                </span>
              )}
              <span
                className={`filter-bar__chevron${isExpanded ? ' filter-bar__chevron--expanded' : ''}`}
                aria-hidden="true"
              >
                <svg width="14" height="14" viewBox="0 0 12 12">
                  <path d="M2 4 L6 8 L10 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
            </>
          )}
        </div>
      </div>

      {hasPanel && isExpanded && (
        <div className="filter-bar__content">
          {panelFields.map(field => (
            <div key={field.key} className={`filter-bar__field ${field.type === 'date' ? 'filter-bar__field--date' : ''}`}>
              <label className="filter-bar__label">{t(field.labelKey)}</label>
              {renderFilterControl(field)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
